import React from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ListItemDetails from './ListItemDetails';
import {
  colors,
  book,
  medium,
  heavy,
  black,
  lightInter,
  mediumInter,
  semiBold,
  regular,
} from '@/styles';
import { RadioButton, ClickableElement } from '@/atoms';
import { Delete, Link, Envelope, Template, Transcript, Preview, Edit } from '@/svgs';
import { getIsMacSafari } from '@/functions';

/**
 * @typedef {import('./ListItem').ListItemProps} ListItemProps
 */

/** @type {MenuItem[]} */
const defaultMenuItems = [];

/**
 * Enable copy menu items for this item
 *
 * Note: When the user is in a video for a team (aka shared video), plus on Mac/Safari
 * (including Mac Outlook Desktop) clicking Copy Link actually copies the video to their
 * account but does NOT copy the link afterwards. Part of EX-10638 we are changing the name
 * of Copy Link and removing Copy for Email under these specific conditions.
 *
 * @typedef {{ label: string; value: string; icon: unknown; size: number; }} MenuItem
 * @param {MenuItem[]} menuItems
 * @param {{ team: string }} props
 * @returns {MenuItem[]}
 */
function enableCopyMenuItems(menuItems, props) {
  const menuForTeams = Boolean(props && props.team !== '');

  if (menuForTeams && getIsMacSafari()) {
    menuItems.unshift({
      label: 'Copy to My Videos',
      value: 'copy_url_video',
      icon: Link,
      size: 12 / 2.85,
    });
  } else {
    menuItems.unshift({
      label: 'Copy for Email',
      value: 'copy_html',
      icon: Envelope,
      size: 10 / 2.85,
    });
    menuItems.unshift({ label: 'Copy Link', value: 'copy_url', icon: Link, size: 12 / 2.85 });
  }

  return menuItems;
}

/**
 * @param {MenuItem[]} menuItems
 * @returns {MenuItem[]}
 */
function enablePreviewMenuItem(menuItems) {
  menuItems.unshift({ label: 'Preview', value: 'preview_video', icon: Preview, size: 10 / 2.85 });
  return menuItems;
}

/**
 * @param {MenuItem[]} menuItems
 * @returns {MenuItem[]}
 */
function enableDeleteMenuItem(menuItems) {
  menuItems.unshift({ label: 'Delete', value: 'delete', icon: Delete, size: 13 / 2.85 });
  return menuItems;
}

/**
 * @param {MenuItem[]} menuItems
 * @returns {MenuItem[]}
 */
function enableSendWithTemplate(menuItems) {
  menuItems.unshift({
    label: 'Send with Template',
    value: 'send_via_bombbomb',
    icon: Template,
    size: 12 / 2.85,
  });
  return menuItems;
}

/**
 * @param {MenuItem[]} menuItems
 * @returns {MenuItem[]}
 */
function enableCopyTranscription(menuItems) {
  menuItems.unshift({
    label: 'Copy Transcript',
    value: 'copy_transcript',
    icon: Transcript,
    size: 12 / 2.85,
  });
  return menuItems;
}

/**
 * @param {MenuItem[]} menuItems
 * @returns {MenuItem[]}
 */
function enableVideoEditMenuItem(menuItems) {
  menuItems.unshift({
    label: 'Edit Video',
    value: 'edit_video',
    icon: Edit,
    size: 12 / 2.85,
  });
  return menuItems;
}

function canBeShared(specificCondition, props) {
  if (!specificCondition) {
    return false;
  }
  if (!props.showReviewStatus && !props.video.reviewStatus) {
    return true;
  }
  return props.video.reviewStatus === 'APPROVED' || !props.video.reviewStatus;
}

/**
 * @param {ListItemProps} props
 * @returns {MenuItem[]}
 */
export function getMenuItems(props) {
  return [
    {
      handler: enableDeleteMenuItem,
      condition: props.enableDeleteMenuItem,
    },
    {
      handler: enableVideoEditMenuItem,
      condition: props.enableVideoEdit,
    },
    {
      handler: enableSendWithTemplate,
      condition: canBeShared(props.enableSendWithTemplate, props),
    },
    {
      handler: enableCopyTranscription,
      condition: canBeShared(props.videoTranscription, props),
    },
    {
      handler: enableCopyMenuItems,
      condition: canBeShared(props.enableCopyMenuItems, props),
    },
    {
      handler: enablePreviewMenuItem,
      condition: props.layout === 'compact',
    },
  ]
    .filter((handler) => handler.condition)
    .reduce(
      (enabledMenuItems, menuItemHandler) => menuItemHandler.handler(enabledMenuItems, props),
      [...defaultMenuItems],
    );
}

/**
 * ListItem component
 *
 * @param {ListItemProps} props
 */
function ListItem(props) {
  const menuItems = getMenuItems(props);

  const containerSizeClass =
    props.containerSize !== '' ? `list-item-container--${props.containerSize}` : '';

  const menuDirection = !props.enableUpwardMenus ? 'down' : props.menuDirection;

  if (props.layout === 'compact') {
    return (
      <div className={`list-item-parent ${props.className} layout--${props.layout}`}>
        <div
          className={classNames('list-item-container', containerSizeClass, {
            'brand--new': props.isRebrand,
          })}
        >
          <ListItemDetails {...props} menuItems={menuItems} menuDirection={menuDirection} />
        </div>
      </div>
    );
  }

  return (
    <ClickableElement
      className={classNames(props.className, `layout--${props.layout}`, {
        disabled: props.disabled,
        'brand--new': props.isRebrand,
      })}
      onClick={props.onSelect}
    >
      <div className={classNames('list-item-container', containerSizeClass)}>
        <div className="radio-button">
          <RadioButton
            onChange={(event) => {
              event.stopPropagation();
              props.onSelect();
            }}
            selected={props.selected}
            type={props.isRebrand ? 'square' : 'circle'}
            selectedColor={props.isRebrand ? colors.electricBlue : colors.kermit}
            unselectedColor={props.isRebrand ? colors.silver : colors.silverSandAlt}
            size={props.isRebrand ? '5' : '5.9'}
            disabled={props.disabled || !props.video.status}
          />
        </div>
        <ListItemDetails {...props} menuItems={menuItems} menuDirection={menuDirection} />
      </div>
    </ClickableElement>
  );
}

ListItem.propTypes = {
  /** Emotion base style */
  className: PropTypes.string.isRequired,
  /** Title of list item */
  title: PropTypes.string,
  /** Subtitle of list item */
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** is this item disabled */
  disabled: PropTypes.bool,
  /** callback for selection toggle */
  onSelect: PropTypes.func,
  /** is this item currently selected */
  selected: PropTypes.bool,
  /** Video object */
  video: PropTypes.shape({
    status: PropTypes.oneOf([
      'UPLOADING',
      'UPLOAD_FAILED',
      'ENCODING',
      'ENCODING_FAILED',
      'PROCESSING',
      'READY',
    ]),
    progress: PropTypes.number,
    thumbnail: PropTypes.string,
    ctaLink: PropTypes.string,
    ctaTitle: PropTypes.string,
    reviewStatus: PropTypes.oneOf(['APPROVED', 'REJECTED', 'PENDING', undefined]),
  }),
  /** Team name */
  team: PropTypes.string,
  /** call back for drop down actions */
  handleDropDown: PropTypes.func,
  /** enable copy menu items */
  enableCopyMenuItems: PropTypes.bool,
  /** enable delete menu items */
  enableDeleteMenuItem: PropTypes.bool,
  /** called when a list item menu is opened */
  onMenuOpen: PropTypes.func,
  /** Layout style choice */
  layout: PropTypes.oneOf(['default', 'compact']),
  /** Size indicator for parent component */
  containerSize: PropTypes.oneOf(['', 'w360', 'chrome', 'small', 'xsmall', 'mobile']),
  /** enable send with template menu item */
  enableSendWithTemplate: PropTypes.bool,
  /** allow menus to open up */
  enableUpwardMenus: PropTypes.bool,
  /** enables the Edit Video menu item */
  enableVideoEdit: PropTypes.bool,
  /** determine which direction the list item menu should open */
  menuDirection: PropTypes.oneOf(['down', 'up']),
  /** transcription of current video */
  videoTranscription: PropTypes.string,
  /** Use new branding */
  isRebrand: PropTypes.bool,
  /** should show list item menu */
  shouldShowMenu: PropTypes.bool,
  /** Show video review status indicator */
  showReviewStatus: PropTypes.bool,
};

ListItem.defaultProps = {
  containerSize: '',
  disabled: false,
  enableCopyMenuItems: false,
  enableDeleteMenuItem: false,
  enableSendWithTemplate: false,
  enableUpwardMenus: false,
  enableVideoEdit: true,
  handleDropDown: () => {},
  layout: 'default',
  menuDirection: 'down',
  onMenuOpen: () => {},
  onSelect: () => {},
  selected: false,
  subtitle: '',
  team: '',
  title: '',
  video: {},
  videoTranscription: null,
  isRebrand: false,
  shouldShowMenu: true,
  showReviewStatus: false,
};

const fullSizeMargin = `${30 / 16}rem`;
const smallSizeMargin = `${20 / 16}rem`;
const smallInnerMargin = `${15 / 16}rem`;

const styledListItem = styled(ListItem)`
  background: white;
  transition: 0.2s;
  text-align: left;
  position: relative;
  padding: 1.437rem 0;
  white-space: nowrap;

  .list-item-container {
    display: flex;
    align-items: center;
  }

  &.disabled {
    opacity: 0.3;
    cursor: default;
  }

  .radio-button {
    margin: 0 0 0 ${fullSizeMargin};
    vertical-align: top;
  }

  /* xsmall has smaller margins */
  .list-item-container--mobile .radio-button,
  .list-item-container--xsmall .radio-button,
  .list-item-container--chrome .radio-button {
    margin-left: ${smallSizeMargin};
    svg {
      width: ${16.75 / 16}rem;
      height: ${16.75 / 16}rem;
    }
  }

  .thumbnail {
    background: ${colors.whiteSmoke};
    flex-shrink: 0;
    min-height: 3rem;
    margin-left: 1.3125rem;
    width: 5.625rem;
    text-align: center;
    position: relative;
    .shared-icon {
      position: absolute;
      background: white;
      border-radius: 50%;
      display: flex;
      height: 16px;
      width: 16px;
      align-items: center;
      box-shadow: 0px 3px 6px #00000029;
      bottom: 3px;
      right: 4px;
      svg {
        display: block;
        margin: 0 auto 1px;
      }
    }
    .placeholder {
      height: 100%;
    }
  }

  .list-item-container--mobile .thumbnail,
  .list-item-container--xsmall .thumbnail,
  .list-item-container--chrome .thumbnail {
    margin-left: ${smallInnerMargin};
    width: ${76 / 16}rem;
  }

  .info {
    text-align: left;
    flex-shrink: 1;
    margin-left: 1.3125rem;
    margin-right: auto;
    min-width: 0;

    .review-status {
      display: block;
      width: fit-content;
      ${mediumInter()}
      background: ${colors.mist};
      color: ${colors.shadow};
      font-size: ${10 / 16}rem;
      padding: 4px 10px;
      border-radius: 2px;
      line-height: 12px;
      margin-bottom: 2px;
    }

    .title {
      ${heavy()}
      font-size: 0.9375rem;
      color: ${colors.boulder};
      margin-top: 0;
      margin-bottom: 0.5rem;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .title svg {
      margin-left: 0.5rem;
    }

    .subtitle {
      ${medium()}
      font-size: 0.8125rem;
      color: ${colors.silverSand};
      margin-top: 0;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;

      .created-date {
        ${book()}
      }
    }

    .team {
      ${book()}
      color: ${colors.silverSand};
      font-size: 0.75rem;
      margin-top: 0.5rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .cta-badge {
      ${black()}
      position: relative;
      background: ${colors.taosSky};
      display: inline-block;
      width: 1.625rem;
      font-size: 0.5rem;
      border-radius: 0.0625rem;
      color: ${colors.white};
      text-align: center;
      letter-spacing: 0.01rem;
      line-height: 1.5;

      .tooltip {
        ${medium()}
        font-size: 0.625rem;
        width: 11.5625rem;
        height: 2.6875;
        background-color: ${colors.fog};
        color: ${colors.boulder};
        text-align: left;
        border-radius: 0.375rem;
        position: absolute;
        z-index: 1;
        opacity: 0;
        transition: opacity 0.3s;
        box-shadow: 0 0.1875rem 0.375rem ${colors.boxShadowDark};
        top: -3.1rem;
        margin-left: -1.375rem;
        white-space: normal;
        padding: 0.4375rem;
        display: none;

        &::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 7%;
          margin-left: -0.3125rem;
          border-width: 0.3125rem;
          border-style: solid;
          border-color: ${colors.fog} transparent transparent transparent;
        }
      }

      &:hover {
        .tooltip {
          opacity: 1;
          display: inline-block;
        }
      }
    }

    ${(props) => {
      if (!props.video?.status) {
        return `
        .title {

        }

        .subtitle {
          width: 70%;
          height: 0.5rem;
          background: ${colors.whiteSmoke};
        }

        .team {
          width: 100%;
          height: 0.5rem;
          background: ${colors.whiteSmoke};
          margin-top: 0.2rem;
        }
  `;
      }
      return '';
    }}
  }

  .list-item-container--mobile .info,
  .list-item-container--xsmall .info,
  .list-item-container--chrome .info {
    margin-left: ${smallInnerMargin};

    .title {
      font-size: ${13 / 16}rem;
      margin-bottom: ${7 / 16}rem;
      line-height: ${15 / 16}rem;
    }
    .subtitle,
    .team {
      font-size: ${11 / 16}rem;
    }

    .team {
      margin-top: 0.1rem;
    }
  }

  &.layout--default {
    .list-item-container--mobile .info .cta-badge .tooltip,
    .list-item-container--xsmall .info .cta-badge .tooltip,
    .list-item-container--chrome .info .cta-badge .tooltip,
    .list-item-container--w360 .info .cta-badge .tooltip {
      font-size: ${11 / 16}rem / ${15 / 16}rem;
      height: ${60 / 16}rem;
      top: -4.1rem;
    }
    .list-item-container--mobile .info .cta-badge:hover .tooltip,
    .list-item-container--xsmall .info .cta-badge:hover .tooltip,
    .list-item-container--chrome .info .cta-badge:hover .tooltip,
    .list-item-container--w360 .info .cta-badge:hover .tooltip {
      width: ${123 / 16}rem;
    }
  }

  .menu {
    display: inline-block;
    text-align: center;
    margin-right: ${fullSizeMargin};
  }

  .list-item-container--mobile .menu,
  .list-item-container--xsmall .menu,
  .list-item-container--chrome .menu {
    margin-right: ${smallSizeMargin};

    .list-item--menu-dropdown {
      margin-left: ${10 / 16}rem;
      width: 23px;
    }
  }

  &.layout--compact {
    padding: 1.125rem 0;

    .title {
      font-size: ${14 / 16}rem;
      ${'' /* https://developer.mozilla.org/en-US/docs/Web/CSS/-webkit-line-clamp */}
      display: -webkit-box;
      white-space: normal;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      line-height: ${18 / 16}rem;
      margin-bottom: 0.3rem;
    }

    .brand--new > .info > .title {
      ${semiBold()}
    }

    .subtitle,
    .team {
      font-size: ${10 / 16}rem;
    }

    .brand--new > .info > .subtitle {
      ${regular()}
    }

    .thumbnail {
      margin-left: ${fullSizeMargin};
      width: ${92 / 16}rem;
    }

    /* compact margins are a bit different, remove them from the thumbnail
    and add to the container */
    .list-item-container--mobile .thumbnail,
    .list-item-container--chrome .thumbnail,
    .list-item-container--xsmall .thumbnail {
      margin-left: 0;
    }

    .list-item-container--mobile,
    .list-item-container--chrome,
    .list-item-container--xsmall {
      margin-left: ${smallSizeMargin};
    }

    .dots circle,
    .dots path {
      ${'' /* approved color */}
      fill: #d1d1d1;
    }

    .info {
      min-width: 40%;
    }

    .list-item-container--mobile .info,
    .list-item-container--xsmall .info,
    .list-item-container--chrome .info {
      min-width: 20%;
    }
  }

  &.brand--new {
    .list-item-container {
      .list-item--menu-dropdown {
        ${regular()}
      }
    }

    .info {
      > .title {
        ${semiBold()}
        font-size: 0.875rem;
        color: ${colors.almostBlack};
        margin-bottom: 0.25rem;
      }

      .subtitle {
        ${mediumInter()}
        font-size: 0.625rem;
        color: ${colors.shadow};
        margin-bottom: 0.25rem;

        .created-date {
          ${lightInter()}
        }
      }

      .team {
        ${lightInter()}
      }

      .cta-badge {
        ${mediumInter()}
        background: ${colors.mist};
        color: ${colors.shadow};
        line-height: 1rem;

        .tooltip {
          ${mediumInter()}
        }
      }
    }

    .dots circle,
    .dots path {
      fill: ${colors.disableGrey};
    }
  }
`;

export default styledListItem;
